import { atom, useAtom } from 'jotai';

interface BuildHubCollaboratorData {
    id: string;
    email: string;
    name: string;
    image: string | null;
}

interface BuildHubBoardModalState {
    isOpened: boolean;
    isCreating?: boolean;
    buildHubId: number;
    buildTitle: string;
    buildHubCollaborators: BuildHubCollaboratorData[]; // BuildHub collaborators for access control
    linkedBoardIds: number[]; // Track boards linked in current session
    alreadyLinkedBoardIds: number[]; // Track boards already linked from previous sessions
    linkingBoardId: number | null; // Track which specific board is currently being linked
}

const buildHubBoardModalAtom = atom<BuildHubBoardModalState>({
    isOpened: false,
    isCreating: false,
    buildHubId: 0,
    buildTitle: '',
    buildHubCollaborators: [],
    linkedBoardIds: [],
    alreadyLinkedBoardIds: [],
    linkingBoardId: null,
});

/**
 * Hook for managing BuildHub board modal state and board linking operations.
 * Handles both session-based board linking and existing linked board states.
 */
export const useBuildHubBoardModal = () => {
    const [state, setState] = useAtom(buildHubBoardModalAtom);

    /**
     * Opens the modal for a specific BuildHub with optional already linked board IDs.
     *
     * @param buildHubId - The ID of the BuildHub to link boards to
     * @param buildTitle - The title of the BuildHub project
     * @param alreadyLinkedBoardIds - Array of board IDs that are already linked to this BuildHub
     * @param isCreating - Open the model with creation view
     * @param buildHubCollaborators - BuildHub collaborators for access control
     */
    const openModal = (
        buildHubId: number,
        buildTitle: string,
        alreadyLinkedBoardIds: number[] = [],
        isCreating?: boolean,
        buildHubCollaborators: BuildHubCollaboratorData[] = []
    ) => {
        setState((prev: BuildHubBoardModalState) => ({
            ...prev,
            isOpened: true,
            isCreating,
            buildHubId,
            buildTitle,
            buildHubCollaborators,
            linkedBoardIds: [], // Reset session links
            alreadyLinkedBoardIds, // Set already linked
        }));
    };

    /**
     * Closes the modal and resets state.
     */
    const closeModal = () => {
        setState((prev: BuildHubBoardModalState) => ({
            ...prev,
            isOpened: false,
        }));
    };

    /**
     * Marks a board as being linked (loading state).
     *
     * @param boardId - The ID of the board being linked
     */
    const setBoardLinking = (boardId: number) => {
        setState((prev: BuildHubBoardModalState) => ({
            ...prev,
            linkingBoardId: boardId,
        }));
    };

    /**
     * Clears the linking state.
     */
    const clearBoardLinking = () => {
        setState((prev: BuildHubBoardModalState) => ({
            ...prev,
            linkingBoardId: null,
        }));
    };

    /**
     * Marks a board as linked in the current session (optimistic update).
     *
     * @param boardId - The ID of the board to mark as linked
     */
    const markBoardAsLinked = (boardId: number) => {
        setState((prev: BuildHubBoardModalState) => ({
            ...prev,
            linkedBoardIds: [...prev.linkedBoardIds, boardId],
            linkingBoardId: null, // Clear linking state when marked as linked
        }));
    };

    /**
     * Reverts a board linking operation (used when API call fails).
     *
     * @param boardId - The ID of the board to revert linking for
     */
    const revertBoardLinking = (boardId: number) => {
        setState((prev: BuildHubBoardModalState) => ({
            ...prev,
            linkedBoardIds: prev.linkedBoardIds.filter((id) => id !== boardId),
            linkingBoardId: null, // Clear linking state on error
        }));
    };

    /**
     * Checks if a specific board is currently being linked.
     *
     * @param boardId - The ID of the board to check
     * @returns True if the board is currently being linked, false otherwise
     */
    const isBoardLinking = (boardId: number) => {
        return state.linkingBoardId === boardId;
    };

    /**
     * Checks if a board is linked (either from previous sessions or current session).
     *
     * @param boardId - The ID of the board to check
     * @returns True if the board is linked, false otherwise
     */
    const isBoardLinked = (boardId: number) => {
        return (
            state.alreadyLinkedBoardIds.includes(boardId) ||
            state.linkedBoardIds.includes(boardId)
        );
    };

    /**
     * Gets the count of boards linked in the current session.
     *
     * @returns The number of boards linked in the current session
     */
    const getSessionLinkedCount = () => {
        return state.linkedBoardIds.length;
    };

    return {
        state,
        openModal,
        closeModal,
        setBoardLinking,
        clearBoardLinking,
        markBoardAsLinked,
        revertBoardLinking,
        isBoardLinked,
        isBoardLinking,
        getSessionLinkedCount,
        isOpened: state.isOpened,
        isCreating: state.isCreating,
        buildHubId: state.buildHubId,
        buildTitle: state.buildTitle,
        buildHubCollaborators: state.buildHubCollaborators,
    };
};
